import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Music, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

function CopyButton({ text, label, testId }) {
  const [copied, setCopied] = useState(false);
  const doCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success(`${label} copied to clipboard`);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Button
      data-testid={testId}
      onClick={doCopy}
      size="sm"
      disabled={!text}
      className="h-8 px-3 bg-[#121B30] border border-[#1E2C48] text-amber-200 hover:bg-amber-400/10 hover:text-amber-100 disabled:opacity-40"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      <span className="ml-1.5 text-xs">{copied ? "Copied" : "Copy"}</span>
    </Button>
  );
}

function Counter({ count, max }) {
  const pct = count / max;
  const color = count > max ? "text-red-400" : pct > 0.9 ? "text-amber-400" : "text-slate-400";
  return (
    <span className={`font-mono text-xs ${color}`} data-testid="char-counter">
      {count.toLocaleString()} / {max.toLocaleString()}
    </span>
  );
}

function Field({ title, testIdCopy, copyText, copyLabel, counter, children }) {
  return (
    <div className="rounded-xl border border-[#1E2C48] bg-[#0D1424] overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1E2C48] bg-[#0D1424]/80">
        <div className="flex items-center gap-2">
          <span className="font-label text-amber-400/90">{title}</span>
          {counter}
        </div>
        <CopyButton text={copyText} label={copyLabel} testId={testIdCopy} />
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

export function OutputStudioPanel({ song, loading, error }) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-[#1E2C48] bg-[#0D1424] p-10 flex flex-col items-center justify-center min-h-[320px]">
        <Loader2 className="w-8 h-8 text-amber-300 animate-spin" />
        <p className="mt-4 text-sm text-slate-300">Writing your song with GPT-6…</p>
        <p className="mt-1 text-xs text-slate-500">This can take up to a minute.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="output-error"
        className="rounded-2xl border border-red-500/40 bg-red-500/5 p-6 min-h-[200px] flex flex-col items-center justify-center text-center"
      >
        <p className="text-red-300 font-semibold">Generation problem</p>
        <p className="mt-2 text-sm text-red-200/80 max-w-md">{error}</p>
      </div>
    );
  }

  if (!song) {
    return (
      <div className="rounded-2xl border border-dashed border-[#1E2C48] bg-[#0D1424]/50 p-10 flex flex-col items-center justify-center min-h-[320px] text-center">
        <div className="w-14 h-14 rounded-2xl bg-[#121B30] border border-[#1E2C48] flex items-center justify-center">
          <Music className="w-6 h-6 text-amber-300/70" />
        </div>
        <p className="mt-4 text-slate-200 font-semibold">Your finished song appears here</p>
        <p className="mt-2 text-sm text-slate-400 max-w-sm">
          Pick a preset or build a custom song, then tap Create. Paste the Styles and Lyrics into Suno v6 Custom mode.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Field title="Song Title" testIdCopy="copy-title-button" copyText={song.title} copyLabel="Title">
        <p data-testid="output-song-title" className="font-heading text-lg font-bold text-slate-50">
          {song.title}
        </p>
      </Field>

      <Field
        title="Suno Styles Prompt"
        testIdCopy="copy-styles-button"
        copyText={song.styles}
        copyLabel="Styles"
        counter={<Counter count={song.styles_count ?? song.styles.length} max={1000} />}
      >
        <p data-testid="output-styles-prompt" className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">
          {song.styles}
        </p>
      </Field>

      <Field
        title="Complete Lyrics"
        testIdCopy="copy-lyrics-button"
        copyText={song.lyrics}
        copyLabel="Lyrics"
        counter={<Counter count={song.lyrics_count ?? song.lyrics.length} max={5000} />}
      >
        <pre
          data-testid="output-lyrics-text"
          className="font-body text-sm text-slate-200 whitespace-pre-wrap leading-relaxed max-h-[460px] overflow-y-auto"
        >
          {song.lyrics}
        </pre>
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field title="Exclude" testIdCopy="copy-exclude-button" copyText={song.exclude} copyLabel="Exclude">
          <p data-testid="output-exclude-prompt" className="text-sm text-slate-300 whitespace-pre-wrap">
            {song.exclude || "—"}
          </p>
        </Field>
        <Field title="Arrangement Note" testIdCopy="copy-note-button" copyText={song.note} copyLabel="Note">
          <p data-testid="output-arrangement-note" className="text-sm text-slate-300 whitespace-pre-wrap">
            {song.note || "—"}
          </p>
        </Field>
      </div>

      <p className="text-xs text-slate-500 leading-relaxed px-1">
        Paste <span className="text-amber-300/90">Styles</span> and{" "}
        <span className="text-amber-300/90">Lyrics</span> into Suno v6 Custom mode. This tool writes prompts
        and lyrics only — it does not create audio, and Suno may interpret vocal directions differently between takes.
      </p>
    </div>
  );
}
